import { useState, useEffect, useRef } from "react";
import { Sparkles, X, Send, Loader2, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { toast } from "sonner";

const SUGGESTED = [
  "Which 3rd-party company has the most job cards?",
  "How many JCs are in COMPLETED status by area?",
  "Top 5 stores by active job cards",
  "Count of job cards by coverage (LOCAL vs NATIONAL)",
  "Which collector has the most COMPLETED jobs?",
];

function ResultTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="px-3 py-4 text-xs text-slate-400">No results.</div>;
  }
  // Build columns from union of keys
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
  return (
    <div className="overflow-auto max-h-72 border border-slate-200 rounded-lg">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left p-2 text-[10px] uppercase tracking-wider text-slate-500 font-medium">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100">
              {cols.map((c) => (
                <td key={c} className="p-2 text-slate-700 align-top whitespace-nowrap">
                  {r[c] === null || r[c] === undefined ? <span className="text-slate-300">—</span> : String(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AskCargoDash() {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [conversation, setConversation] = useState([]); // [{question, ok, rows, error, unsupported}]
  const [loading, setLoading] = useState(false);
  const [topQuestions, setTopQuestions] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/ai/top-questions?limit=10")
      .then(({ data }) => setTopQuestions(data || []))
      .catch(() => { /* hide silently */ });
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation, loading]);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setLoading(true);
    const turn = { question: text, loading: true };
    setConversation((c) => [...c, turn]);
    setQuestion("");
    try {
      const { data } = await api.post("/ai/ask", { question: text });
      setConversation((c) => c.slice(0, -1).concat({ question: text, ...data, loading: false }));
      // Refresh top questions silently after a successful ask
      api.get("/ai/top-questions?limit=10").then(({ data: tq }) => setTopQuestions(tq || [])).catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.detail || "AI call failed";
      setConversation((c) => c.slice(0, -1).concat({ question: text, ok: false, error: msg, loading: false }));
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        data-testid="ai-launcher"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-5 z-[60] group flex items-center gap-2 bg-gradient-to-br from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white px-4 h-12 rounded-full shadow-lg shadow-teal-500/30 transition-all"
      >
        <Sparkles size={18} />
        <span className="font-medium text-sm tracking-tight">Ask CargoDash</span>
      </button>

      {/* Side drawer */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div data-testid="ai-drawer" className="fixed top-0 right-0 z-50 h-screen w-full max-w-xl bg-white shadow-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200 bg-gradient-to-r from-teal-50 via-emerald-50 to-white">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-teal-600" />
                <h3 className="font-heading font-semibold text-slate-900 tracking-tight">Ask CargoDash</h3>
                <Badge variant="outline" className="text-[9px] uppercase bg-white text-emerald-700 border-emerald-200">DB-grounded</Badge>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 p-1" data-testid="ai-close">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {conversation.length === 0 && (
                <div className="space-y-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed">
                    <p className="font-semibold text-slate-700 text-sm mb-1">Ask anything about your job cards.</p>
                    <p>I only answer from your live database — no general knowledge, no guessing. Most answers come back as a table.</p>
                  </div>

                  {topQuestions.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2 flex items-center gap-1">
                        <Clock size={11} /> Most-asked
                      </p>
                      <div className="space-y-1.5">
                        {topQuestions.slice(0, 10).map((tq, i) => (
                          <button
                            key={tq.normalized}
                            data-testid={`ai-top-q-${i}`}
                            onClick={() => ask(tq.display)}
                            className="w-full text-left text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-colors flex items-center justify-between gap-2"
                          >
                            <span className="text-slate-700 truncate">{tq.display}</span>
                            <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200 shrink-0">×{tq.asks}</Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Try one of these</p>
                    <div className="space-y-1.5">
                      {SUGGESTED.map((s, i) => (
                        <button
                          key={s}
                          data-testid={`ai-suggested-${i}`}
                          onClick={() => ask(s)}
                          className="w-full text-left text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-colors text-slate-700"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {conversation.map((turn, idx) => (
                <div key={idx} className="space-y-2" data-testid={`ai-turn-${idx}`}>
                  {/* user bubble */}
                  <div className="flex justify-end">
                    <div className="max-w-[88%] bg-teal-600 text-white text-sm rounded-2xl rounded-tr-md px-3 py-2 shadow-sm">
                      {turn.question}
                    </div>
                  </div>
                  {/* answer */}
                  <div className="flex">
                    <div className="max-w-full w-full bg-slate-50 border border-slate-200 text-sm rounded-2xl rounded-tl-md px-3 py-2.5 space-y-2">
                      {turn.loading && (
                        <div className="flex items-center gap-2 text-slate-500 text-xs"><Loader2 size={14} className="animate-spin" /> Searching the database…</div>
                      )}
                      {!turn.loading && turn.ok && (
                        <>
                          <div className="text-xs text-slate-500">
                            <span className="text-emerald-600 font-medium">{turn.row_count ?? (turn.rows?.length || 0)}</span> result{(turn.row_count ?? (turn.rows?.length || 0)) === 1 ? "" : "s"}
                          </div>
                          <ResultTable rows={turn.rows} />
                        </>
                      )}
                      {!turn.loading && !turn.ok && turn.unsupported && (
                        <div className="text-xs text-amber-700">
                          <span className="font-medium">Outside CargoDash scope:</span> {turn.unsupported}
                        </div>
                      )}
                      {!turn.loading && !turn.ok && turn.error && (
                        <div className="text-xs text-red-600">
                          <span className="font-medium">Couldn't answer:</span> {turn.error}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Composer */}
            <div className="border-t border-slate-200 p-3 bg-white">
              <div className="flex gap-2">
                <input
                  data-testid="ai-input"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !loading) ask(); }}
                  placeholder="e.g. Which JCs are still in AGENT status?"
                  className="flex-1 h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
                  disabled={loading}
                />
                <Button
                  data-testid="ai-submit"
                  onClick={() => ask()}
                  disabled={loading || !question.trim()}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-2 px-1">
                Answers are computed from your live database. No general-knowledge text is generated.
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}
